import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { CombinationFilter } from 'components/common/combination-filter-controls';
import {
  restoreSystemCompatibleResults,
  setSystemRecommendationCatalogCodes
} from 'features/structure/structure-slice';
import axios from 'utils/api';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';
import runCompatibilityCheck from 'utils/equipment-compatibility-check';
import { fuelTypeForDerive } from 'utils/recommendation-tiles-logic';
import {
  catalogCodeUnion,
  extractRecommendedCodes,
  flattenWantRecommendations,
  hasRecommendationAnchor,
  isUnanchoredWithoutCodes,
  RecommendationContext,
  RecommendationsResult,
  resolveEffectiveRecommendedCodes,
  wantOnlyFallbackCodes
} from 'utils/recommendations-strip-logic';
import { SystemSortOption } from 'utils/system-sort';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import {
  DuctedCoolingOperatingCostParams,
  ductedCoolingOperatingCostRequestFields
} from './ducted-cooling-operating-cost-params';
import {
  beginDuctedCombinationsFetch,
  DuctedResultSet,
  failDuctedCombinationsFetch,
  finishDuctedCombinationsFetch,
  recordDuctedCombinationsSkipped
} from './ducted-compatibility-slice';
import { representativesDataFrom } from './ducted-representative-payload';
import { fetchNoCurrentSystemCodes } from './no-current-system-codes';

export type FetchDuctedCombinationsParams = {
  systemUniqueId: string;
  // Which of the two Choose Systems result sets this fetch fills (AHA-6681
  // sub-build 4): 'kept' sends the not-replacing component IDs below;
  // 'alternatives' sends keptComponentUniqueIds: [] (ss-api honors the
  // empty array as an explicit all-replacing override).
  resultSet: DuctedResultSet;
  keptComponentUniqueIds: string[];
  systemTypeCodes: string[];
  // Recommended-set fetch universe (AHA-6681 sub-build 3, plumbing for the
  // pnet scoped cache fill): the FULL recommended code set for the system,
  // independent of the chip narrowing in systemTypeCodes. Forwarded by
  // ss-api to pnet, where it scopes the cache universe (v6 keying). Both
  // result sets send the same recommended codes.
  universeCodes: string[];
  filters: CombinationFilter[];
  inStockOnly: boolean;
  // Price-range bounds from the panel's Price Range inputs (null = unset).
  // Null bounds are OMITTED from the POST body (conditional spread below) so
  // default-filter bodies — and their requestBodyHash — stay byte-identical
  // to the pre-price-filter ones.
  priceMin: number | null;
  priceMax: number | null;
  pageNumber: number;
  pageSize: number;
  coolingOperatingCost: DuctedCoolingOperatingCostParams;
  // Sizing-bands relax override (AHA-6796 bands T3). REQUIRED (the
  // tonnageSeed precedent): every dispatcher passes it explicitly so the
  // request-body hash can't fork between callers. true (the default state)
  // OMITS the key from the POST body — default bodies stay byte-identical
  // to the pre-T3 ones; false sends applySizingBands: false.
  applySizingBands: boolean;
  // The GUI-resolved tonnage range, from the SAME resolver the add path
  // sends into Phase 6. Without it a home with no room-by-room design loads
  // gives the server nothing to compute design-load coverage against, and
  // the card ends up quoting a different figure than the system it becomes.
  // REQUIRED for the same reason as applySizingBands; null omits the key.
  tonnageSeed: { min: number; max: number } | null;
  // The Choose Systems "Sort by" selection (ui.systemSort). ALWAYS sent: the
  // server pages the whole ducted pool in this order — the fit tiers
  // for 'recommended', a single card figure otherwise — so the dropdown is
  // exactly what the grid shows and changing it refetches page one. REQUIRED
  // for the same reason as applySizingBands — every dispatcher passes it so
  // the request-body hash cannot fork between callers.
  ductedSort: SystemSortOption;
};

// The EXACT request body the combinations thunk POSTs — and hashes — for a
// given params object (AHA-6681 sub-build 5). Exported so the pre-warm hook
// can compute the identical requestBodyHash without dispatching (its dedupe
// against already-warm slice entries) and so tests can pin the pre-warm /
// page-first-fetch hash identity. Key order matters: JSON.stringify of this
// object IS the slice's requestBodyHash.
export const ductedCombinationsRequestBody = (
  params: FetchDuctedCombinationsParams
) => ({
  systemUniqueId: params.systemUniqueId,
  // The alternatives set is unconstrained by kept equipment by definition —
  // enforced here so callers can pass the kept IDs uniformly.
  keptComponentUniqueIds:
    params.resultSet === 'alternatives' ? [] : params.keptComponentUniqueIds,
  systemTypeCodes: params.systemTypeCodes,
  universeCodes: params.universeCodes,
  activeOnly: true,
  inStockOnly: params.inStockOnly,
  ...(params.priceMin !== null ? { priceMin: params.priceMin } : {}),
  ...(params.priceMax !== null ? { priceMax: params.priceMax } : {}),
  // Only the relaxed state enters the body (same pattern as the price
  // bounds): default bodies — and their requestBodyHash — stay
  // byte-identical to the pre-T3 ones.
  ...(params.applySizingBands === false ? { applySizingBands: false } : {}),
  ...(params.tonnageSeed ? { tonnageSeed: params.tonnageSeed } : {}),
  pageNumber: params.pageNumber,
  pageSize: params.pageSize,
  filters: params.filters,
  // In the body — and so in the hash — unconditionally: a dropdown change
  // must refetch page one in the new order, and the default ('recommended')
  // is still a server order rather than pnet's ID order.
  ductedSort: params.ductedSort,
  ...ductedCoolingOperatingCostRequestFields(params.coolingOperatingCost)
});

// The requestBodyHash the thunk will record for these params — the slice
// dedupe/latest-wins currency shared by Choose Systems and the pre-warm.
export const ductedCombinationsRequestHash = (
  params: FetchDuctedCombinationsParams
): string => JSON.stringify(ductedCombinationsRequestBody(params));

// Full compatible-combinations fetch for one (system, resultSet) on Choose
// Systems (AHA-6681 CS-1, dual sets in sub-build 4). Request shape mirrors
// the CCB modal's fetch: activeOnly fixed to true (the fresh-open default),
// inStockOnly from the panel's existing toggle, no pnet sortBy — the page
// order is the server's order of the ducted pool under ductedSort. The
// response also carries the rail's Recommended row per requested system
// type (representativesByType), ranked from the same pool as the page, so
// page one fills the per-type representative entries too — there is no
// separate representatives request.
export const fetchDuctedCompatibleCombinations = createAsyncThunk(
  'ductedCompatibility/fetchCombinations',
  async (params: FetchDuctedCombinationsParams, { dispatch, getState }) => {
    const { systemUniqueId, resultSet, pageNumber } = params;

    const body = ductedCombinationsRequestBody(params);
    // A tail page extends the loaded rows; the Recommended cards stay put.
    const append = pageNumber > 1;

    // All equipment replacing with no system-type codes: the backend guards
    // this to empty (an unanchored search needs a type anchor), so skip the
    // POST and record the honest zero (same contract as runCompatibilityCheck).
    if (
      isUnanchoredWithoutCodes(
        body.keptComponentUniqueIds,
        body.systemTypeCodes
      )
    ) {
      dispatch(recordDuctedCombinationsSkipped({ systemUniqueId, resultSet }));
      return;
    }

    const requestBodyHash = JSON.stringify(body);

    // Dedupe an identical in-flight request (effect re-runs with unchanged
    // inputs must not double-POST).
    const state = getState() as RootState;
    const existing =
      state.ductedCompatibility.resultsBySystem[systemUniqueId]?.[resultSet];
    if (
      existing?.status === 'loading' &&
      existing.requestBodyHash === requestBodyHash
    ) {
      return;
    }

    dispatch(
      beginDuctedCombinationsFetch({
        systemUniqueId,
        resultSet,
        requestBodyHash,
        // Same page-one/tail split the finish below applies: a tail page
        // extends the loaded rows, page one replaces them (and the rail
        // shows skeletons in the meantime).
        append,
        // Page one carries the Recommended row for every type it covers.
        ...(append ? {} : { representativeTypeCodes: body.systemTypeCodes })
      })
    );
    try {
      const response = await axios.post(
        '/existing-equipment/compatible-combinations',
        body
      );
      dispatch(
        finishDuctedCombinationsFetch({
          systemUniqueId,
          resultSet,
          requestBodyHash,
          // Tail pages append onto the loaded rows (AHA-6681 CS-2 show-more);
          // page 1 keeps the replace semantics.
          append,
          ...(append
            ? {}
            : {
                representativesByType: representativesDataFrom(
                  response.data.representativesByType
                )
              }),
          data: {
            combinations: response.data.compatibleCombinations ?? [],
            totalCount: response.data.metadata?.totalCount ?? 0,
            systemTypes: response.data.metadata?.systemTypes ?? [],
            filterOptions: response.data.filterOptions ?? [],
            modelSpecs: response.data.modelSpecs ?? {},
            productData: response.data.productData ?? {},
            accessoryData: response.data.accessoryData ?? {},
            // Reduction-based server report (AHA-6796 bands T3): absent
            // (gate off / no constraint) lands false.
            sizingApplied: response.data.sizingApplied === true,
            // The band fit nothing and the server sent the closest sizes.
            sizingNearestFit: response.data.sizingNearestFit === true,
            // Provenance: this data came from a relaxed-body request. Append
            // finishes (pageNumber > 1) carry it too — same-body chain, so
            // the flag is consistent across pages.
            sizingRelaxed: params.applySizingBands === false,
            // Reduction-based server report for the project ZIP's
            // ProductRegulations rules: absent (no ZIP / older server) lands
            // false and no regulations.
            regulationsApplied: response.data.regulationsApplied === true,
            appliedRegulations: response.data.appliedRegulations ?? []
          }
        })
      );
    } catch (error) {
      console.error(
        `[ducted-compatibility] combinations fetch failed for system ${systemUniqueId} (${resultSet}):`,
        error
      );
      dispatch(
        failDuctedCombinationsFetch({
          systemUniqueId,
          resultSet,
          requestBodyHash
        })
      );
    }
  }
);

export type DeriveDuctedRecommendedSummaryParams = {
  systemUniqueId: string;
  keptComponentUniqueIds: string[];
  componentsKey: string;
};

// The result sets in play for a system: the kept-constrained set only
// exists when at least one component is kept (AHA-6681 sub-build 4 — the
// kept fetch is skipped entirely when zero components are kept).
export const resultSetsFor = (
  keptComponentUniqueIds: string[]
): DuctedResultSet[] =>
  keptComponentUniqueIds.length > 0
    ? ['kept', 'alternatives']
    : ['alternatives'];

// Missing-summary fallback for Choose Systems (AHA-6681 sub-build 3):
// fresh hydration and old rows can reach the page with wants saved but no
// persisted LES-4 summary — the recommended bound is then unresolvable and
// nothing may be fetched. This thunk re-derives it exactly the way the
// Equipment Details step would have: the recommendation context GET supplies
// the saved/derived strip inputs, the recommendations POST turns them into
// recommended codes, and runCompatibilityCheck turns the codes into the
// stored + persisted recommended-set summary (healing the old row). The
// fetch hook then finds the bound on its next pass and fetches normally.
// Any dead end (no wants, no anchor, request failure) records the honest
// ducted skip so the rail's empty state renders instead of a silent limbo.
export const deriveDuctedRecommendedSummary = createAsyncThunk(
  'ductedCompatibility/deriveRecommendedSummary',
  async (
    params: DeriveDuctedRecommendedSummaryParams,
    { dispatch, getState }
  ) => {
    const { systemUniqueId, keptComponentUniqueIds, componentsKey } = params;

    // A summary appeared since the dispatch (a live check or a concurrent
    // derivation won the race): the bound is resolvable — nothing to derive.
    const state = getState() as RootState;
    if (state.structure.compatibleResultsBySystem[systemUniqueId]) return;

    // The same tonnage seed the EE wrapper's refetch would pass (shared
    // resolver on the same Redux inputs) — resolved LAZILY at each
    // runCompatibilityCheck dispatch below, not at thunk start: the awaited
    // context/derive/recommendations requests leave a window where the
    // tonnage record, the components, or coolingBtu can change, and a seed
    // captured up front would persist a wrong-filtered summary and fork the
    // dedupe requestBodyHash from the wrapper's.
    const currentTonnageSeed = () => {
      const seedState = getState() as RootState;
      return resolveSystemTonnageSeed({
        system: seedState.structure.existingSystems.find(
          (system) => system.uniqueId === systemUniqueId
        ),
        record:
          seedState.structure.recommendationSelectionsBySystem[systemUniqueId]
            ?.tonnage ?? null,
        coolingBtu: selectDuctedPairLoads(seedState, systemUniqueId).cooling
      });
    };

    try {
      const contextResponse = await axios.get<RecommendationContext>(
        `/existing-equipment/recommendation-context/${systemUniqueId}`
      );
      const context = contextResponse.data;
      // Keep the full-universe catalog mirror live on the CS-only path too
      // (AHA-6796 fuel vocabulary): this healer runs when no strip is
      // mounted, and it already holds the context response.
      dispatch(
        setSystemRecommendationCatalogCodes({
          systemUniqueId,
          codes: catalogCodeUnion(context?.systemTypes)
        })
      );
      const saved = context?.savedSelections ?? null;
      const wantIds = saved?.wantIds ?? [];

      // No wants: the gateway has nothing to recommend — honest skip, the
      // rail steers back to the step.
      if (wantIds.length === 0) {
        resultSetsFor(keptComponentUniqueIds).forEach((resultSet) => {
          dispatch(
            recordDuctedCombinationsSkipped({ systemUniqueId, resultSet })
          );
        });
        return;
      }

      // Fingerprint gate for the union-virtual healing branches below
      // (AHA-6681 tile panel): a record saved under a different inventory
      // (mismatch, or a legacy row without a fingerprint) must not be
      // restored or replayed — the EE-visit overwrite/reseed path owns
      // stale records, so it falls through to the anchor check as before.
      const fingerprintMatches =
        !saved || saved.fingerprint === context?.derived?.fingerprint;

      // Branch A — persisted LES-4 summary: restore it instead of
      // re-deriving (zero POSTs, the EE wrapper's exact restore hop). The
      // fetch hook re-runs off the restored bound and fetches normally.
      if (saved?.lastResults && fingerprintMatches) {
        const { fetchedAt, alternatives, kept } = saved.lastResults;
        dispatch(
          restoreSystemCompatibleResults({
            systemUniqueId,
            // Restored entries carry no requestBodyHash on purpose — the
            // first live check after a restore always refetches once.
            results: {
              alternatives: { status: 'loaded', fetchedAt, ...alternatives },
              ...(kept
                ? { kept: { status: 'loaded', fetchedAt, ...kept } }
                : {})
            }
          })
        );
        return;
      }

      // Saved strip inputs win when present (explicit null = user cleared);
      // absent keys fall back to the derived values — the strip's own
      // restore convention.
      let haveId =
        saved && 'haveId' in saved
          ? saved.haveId ?? null
          : context?.derived?.currentSystemTypeId ?? null;
      const refrigerant =
        saved && 'refrigerant' in saved
          ? saved.refrigerant ?? null
          : context?.derived?.refrigerant ?? null;
      const age = saved?.age ?? null;
      let candidateIds = context?.derived?.candidateSystemTypeIds ?? [];

      // Branch B — anchorless union-virtual record without lastResults
      // (legacy-flat rows the read-side parse dropped): replay the strip's
      // derive from the persisted tile codes to recover the anchor, exactly
      // the request the tile panel would fire. Runs only after A so a
      // restorable summary never pays this POST.
      const virtualCodes = saved?.virtualCodes ?? [];
      if (
        !hasRecommendationAnchor(haveId, candidateIds) &&
        fingerprintMatches &&
        virtualCodes.length > 0
      ) {
        const deriveResponse = await axios.post<{
          currentSystemTypeId: number | null;
          candidateSystemTypeIds: number[];
        }>('/existing-equipment/derive-current-system', {
          componentCodes: virtualCodes,
          fuelType: fuelTypeForDerive(saved?.virtualFuel ?? null)
        });
        haveId = deriveResponse.data?.currentSystemTypeId ?? null;
        candidateIds = deriveResponse.data?.candidateSystemTypeIds ?? [];
      }

      // No derivation anchor (no existing equipment, or none the derivation
      // could identify), but wants ARE saved (the empty case returned
      // above): resolve them in the API's no-current-system mode — the same
      // request the strip fires — and bound the check to those codes. A
      // response without codes (or a failed request) falls back to the
      // union of the selected want types' own compatibility codes (AHA-6681
      // want-only fallback). Either bound runs the check exactly like a
      // recommended set would (runCompatibilityCheck stores AND persists
      // the LES-4 summary, healing the row); only all-unmapped wants keep
      // today's honest skip. No fingerprint gate, consistent with the
      // anchored recs-POST path below consuming saved wantIds ungated.
      if (!hasRecommendationAnchor(haveId, candidateIds)) {
        const unanchoredCodes = resolveEffectiveRecommendedCodes(
          await fetchNoCurrentSystemCodes(wantIds),
          wantOnlyFallbackCodes(wantIds, context?.systemTypes)
        );
        if (unanchoredCodes.length > 0) {
          await dispatch(
            runCompatibilityCheck({
              systemUniqueId,
              keptComponentUniqueIds,
              systemTypeCodes: unanchoredCodes,
              componentsKey,
              tonnageSeed: currentTonnageSeed()
            })
          );
          return;
        }
        resultSetsFor(keptComponentUniqueIds).forEach((resultSet) => {
          dispatch(
            recordDuctedCombinationsSkipped({ systemUniqueId, resultSet })
          );
        });
        return;
      }

      const recsResponse = await axios.post<RecommendationsResult>(
        '/existing-equipment/recommendations',
        {
          // Blank-as-union anchor, same body the strip posts (DECISION 2).
          ...(haveId == null
            ? { currentSystemTypeIds: candidateIds }
            : { currentSystemTypeId: haveId }),
          currentSystemRefrigerant: refrigerant,
          currentSystemAge: age,
          desiredSystemTypeIds: wantIds
        }
      );
      const codes = extractRecommendedCodes(
        flattenWantRecommendations(recsResponse.data?.recommendationsByWant)
      );
      // runCompatibilityCheck handles empty codes itself (honest zero, no
      // POST) and persists the summary — the derived bound survives reloads.
      await dispatch(
        runCompatibilityCheck({
          systemUniqueId,
          keptComponentUniqueIds,
          systemTypeCodes: codes,
          componentsKey,
          tonnageSeed: currentTonnageSeed()
        })
      );
    } catch (error) {
      console.error(
        `[ducted-compatibility] recommended-summary derivation failed for system ${systemUniqueId}:`,
        error
      );
      resultSetsFor(keptComponentUniqueIds).forEach((resultSet) => {
        dispatch(
          recordDuctedCombinationsSkipped({ systemUniqueId, resultSet })
        );
      });
    }
  }
);
